import * as React from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/track";
import { useSession, clearSessionMarker } from "@/hooks/use-session";
import {
  handleAuthFailure,
  installAuthFailureObserver,
  registerAuthFailureHandler,
  setSessionPresence,
} from "@/lib/auth-recovery";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const router = useRouter();
  const queryClient = useQueryClient();

  // One recovery path for every expired or missing session, wherever it is
  // discovered: a 401 from a server function, a sign-out, or a token refresh
  // that came back empty. Clears local state, then sends the user to sign in
  // with the current path so they land back where they were.
  React.useEffect(() => {
    return registerAuthFailureHandler(() => {
      const loc = router.state.location;
      // Loop guard: already on the sign-in page, nothing to do.
      if (loc.pathname.startsWith("/auth")) return;
      const back = `${loc.pathname}${loc.searchStr ?? ""}`;
      clearSessionMarker();
      void queryClient.cancelQueries();
      queryClient.clear();
      void supabase.auth.signOut().catch(() => undefined);
      void router.navigate({
        to: "/auth",
        search: { mode: "signin", redirect: back },
        replace: true,
      });
    });
  }, [router, queryClient]);

  // Catch 401s at the transport layer too: a loader that hits one never
  // surfaces a readable auth error to the page.
  React.useEffect(() => installAuthFailureObserver(), []);

  React.useEffect(() => {
    let mounted = true;

    // Subscribe FIRST so we never miss an event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      setSession(nextSession);
      setLoading(false);
      // Remember that a session existed so a later 401 is treated as an
      // expiry. Never cleared: sign-out wipes storage before the handler runs.
      if (nextSession) setSessionPresence(true);
      // A sign-out, or a token refresh that produced no session, means the
      // user can no longer load anything behind the sign-in gate.
      if (event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !nextSession)) {
        handleAuthFailure();
        return;
      }
      // Only invalidate the router when signed-in reality changes.
      // TOKEN_REFRESHED and INITIAL_SESSION fire on tab focus / mount and
      // would otherwise re-run beforeLoad on every route, remounting
      // ephemeral flows like the signup code-entry step.
      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        router.invalidate();
      }
    });

    // Then hydrate the initial session.
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
      if (data.session) setSessionPresence(true);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [router]);

  const signOut = React.useCallback(async () => {
    // Fire-and-forget. Emit before the session is torn down so the event
    // is attributed to the user that initiated the logout.
    track("logout");
    clearSessionMarker();
    await supabase.auth.signOut();
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ session, user: session?.user ?? null, loading, signOut }),
    [session, loading, signOut],
  );

  // Mark a 30-min browser session on first authed render. The hook is a
  // no-op when the user id is null.
  useSession(session?.user?.id ?? null);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}